import React, { useEffect, useState } from 'react';
import Tooltip from './common/Tooltip';
import { toast } from 'react-hot-toast';
import { formatCurrency, formatAmount, formatChangedAmount, getChangeColorClass, formatChangedPercentage, formatPrice, formatPositionAge, parseFloatFromString, subtractDecimalStrings } from '../utils/formatters';
import { TransactionType } from '../utils/enums';
import { usePortfolioContext } from '../contexts/PortfolioContext';

const ADVANCED_STATS_STORAGE_KEY = 'position_advanced_stats_expanded';

const numeric = (value) => parseFloatFromString(value || '0') || 0;
const safePercentage = (numerator, denominator) => {
  const den = numeric(denominator);
  if (!den) return null;
  return ((numeric(numerator) / den) * 100).toFixed(2);
};

const InfoLabel = ({ children, title }) => (
  <Tooltip
    title={title}
    position="bottom"
    trigger="mouseenter focus click"
    interactive={false}
    arrow={true}
    theme="dark"
    size="small"
  >
    <span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">
      {children}
    </span>
  </Tooltip>
);

const DetailRow = ({ label, value, valueClass = '', tooltip }) => (
  <div className="flex justify-between gap-3 py-0.5">
    <span className="text-gray-500 dark:text-gray-400">
      {tooltip ? <InfoLabel title={tooltip}>{label}</InfoLabel> : label}
    </span>
    <span className={`font-medium text-right ${valueClass}`}>{value}</span>
  </div>
);

const PositionDetails = ({ position, token }) => {
  const { currentPortfolio } = usePortfolioContext();
  const portfolioCurrency = currentPortfolio?.currency || 'USD';
  const currencySymbol = (() => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: portfolioCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
        .formatToParts(0)
        .find(p => p.type === 'currency')?.value || '$';
    } catch (_) {
      return '$';
    }
  })();
  const [advancedStatsExpanded, setAdvancedStatsExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(ADVANCED_STATS_STORAGE_KEY);
      return stored !== null ? JSON.parse(stored) : false;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(ADVANCED_STATS_STORAGE_KEY, JSON.stringify(advancedStatsExpanded));
    } catch (_) {}
  }, [advancedStatsExpanded]);

  const tokenData = token || {};
  const sellTransactionCount = (position?.transactions || []).filter(tx =>
    (tx.transactionType === TransactionType.SELL || tx.transactionType === TransactionType.TRANSFER_OUT) && tx?.hidden !== true
  ).length;
  const hasRealizedSells = sellTransactionCount > 0;
  const visibleTxCount = (position?.transactions || []).filter(tx => tx?.hidden !== true).length;
  const locationBalances = position?.locationBalances || [];
  const unassignedQuantity = subtractDecimalStrings(position?.holdingsAmount || '0', locationBalances.map(balance => balance.quantity || '0'));
  const hasUnassignedQuantity = parseFloatFromString(unassignedQuantity) > 0;

  if (!position || visibleTxCount === 0) return null;

  const activePnl = position.unrealized?.pnl || '0';
  const activeRoi = position.unrealized?.roi || '0';
  const activeBasis = position.unrealized?.basisCost || position.current?.basisCost || '0';
  const currentValue = position.holdingsValue || '0';
  const totalPnl = position.current?.pnl || '0';
  const allTimeRoi = position.current?.roi || '0';
  const soldProceeds = position.realized?.proceeds || '0';
  const realizedPnl = position.realized?.pnl || '0';
  const grossBuys = position.investedCost || '0';
  const netCapitalIn = Math.max(0, numeric(grossBuys) - numeric(soldProceeds));
  const activeValueVsBasis = safePercentage(currentValue, activeBasis);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Current value</h3>
            <div className="text-xl font-semibold">{formatCurrency(currentValue)}</div>
            <Tooltip
              title={`${position.holdingsAmount} ${tokenData.ticker || 'TOKEN'} - click to copy`}
              position="bottom"
              trigger="mouseenter"
              interactive={false}
              arrow={true}
              theme="dark"
              size="small"
            >
              <div
                className="text-sm text-gray-500 dark:text-gray-400 cursor-pointer border-b border-dashed border-gray-400 dark:border-gray-500 inline-block"
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                    navigator.clipboard
                      .writeText(String(position.holdingsAmount))
                      .then(() => toast.success('Copied to clipboard'))
                      .catch(() => {});
                  }
                }}
              >
                {formatAmount(position.holdingsAmount)} {tokenData.ticker || 'TOKEN'}
              </div>
            </Tooltip>
          </div>

          <div className="md:text-right">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              <InfoLabel title="Profit or loss on the coins you still hold, compared with the remaining FIFO cost basis. This is the clearest hold/cut/add signal after sells and re-buys.">Active PnL</InfoLabel>
            </h3>
            <div className={`text-lg font-semibold ${getChangeColorClass(activePnl)}`}>{formatChangedAmount(activePnl, currencySymbol)}</div>
            <div className={`text-sm ${getChangeColorClass(activeRoi)}`}>
              {formatChangedPercentage(activeRoi)} active ROI
              <span className="mx-2">•</span>
              ({position.unrealized?.multiple || '0'}x)
            </div>
          </div>

          <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            <DetailRow
              label="remaining basis"
              value={formatCurrency(activeBasis)}
              tooltip="The cost basis of the coins you still hold. Past sells are removed using FIFO, so replacement buys do not inflate this number."
            />
            <DetailRow
              label="avg buy"
              value={formatPrice(position.averageBuyPrice)}
              tooltip="Average buy price across buy transactions. Useful context, but remaining basis is usually better for the active position."
            />
            {position.positionAge && (
              <DetailRow
                label="holding"
                value={formatPositionAge(position.positionAge)}
                tooltip={`Oldest remaining FIFO lot since ${new Date(position.positionAge).toLocaleDateString()}`}
              />
            )}
            {locationBalances.length > 0 && (
              <div className="mt-2 border-t border-gray-200 dark:border-gray-600 pt-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">locations</div>
                {locationBalances.map((balance) => (
                  <div key={balance.locationId} className="flex justify-between gap-2">
                    <span className="truncate">{balance.name}</span>
                    <span className="font-mono">{formatAmount(balance.quantity)}</span>
                  </div>
                ))}
                {hasUnassignedQuantity && (
                  <div className="flex justify-between gap-2 text-gray-500 dark:text-gray-400">
                    <span>Unassigned</span>
                    <span className="font-mono">{formatAmount(unassignedQuantity.toString())}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col self-stretch mt-2 text-sm text-gray-600 dark:text-gray-300">
            <DetailRow
              label="all-time PnL"
              value={formatChangedAmount(totalPnl, currencySymbol)}
              valueClass={getChangeColorClass(totalPnl)}
              tooltip="Closed trades plus the current open position: sold proceeds plus current value minus all buys. This can be positive even when the remaining bag is down."
            />
            {hasRealizedSells && (
              <DetailRow
                label="closed trades"
                value={formatChangedAmount(realizedPnl, currencySymbol)}
                valueClass={getChangeColorClass(realizedPnl)}
                tooltip="Profit/loss already locked in by sells."
              />
            )}
            <div className="mt-auto flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setAdvancedStatsExpanded(v => !v)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                aria-expanded={advancedStatsExpanded}
              >
                {advancedStatsExpanded ? 'Hide extra stats' : 'Show extra stats'}
              </button>
            </div>
          </div>
        </div>

        {advancedStatsExpanded && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            <DetailRow
              label="gross buys"
              value={formatCurrency(grossBuys)}
              tooltip="Total amount ever spent buying this token. This can be high after sell-and-rebuy moves."
            />
            <DetailRow
              label="sold"
              value={formatCurrency(soldProceeds)}
              tooltip="Cash received from sells so far."
            />
            <DetailRow
              label="net cash in"
              value={formatCurrency(netCapitalIn.toFixed(2))}
              tooltip="Gross buys minus sells. This is cash-flow context, not a good ROI denominator when previous sells almost paid back the position."
            />
            <DetailRow
              label="all-time ROI"
              value={formatChangedPercentage(allTimeRoi)}
              valueClass={getChangeColorClass(allTimeRoi)}
              tooltip="Total PnL divided by all buys ever. Useful for lifecycle history, but it can look too gentle after sell-and-rebuy moves because both buys remain in the denominator."
            />
            <DetailRow
              label="value / basis"
              value={activeValueVsBasis == null ? '-' : `${activeValueVsBasis}%`}
              tooltip="Current value as a percentage of remaining cost basis. 100% means break-even on the active holding."
            />
            <DetailRow
              label="unrealized"
              value={formatChangedAmount(activePnl, currencySymbol)}
              valueClass={getChangeColorClass(activePnl)}
              tooltip="Same as Active PnL: still-open profit/loss on the remaining coins."
            />
            <DetailRow
              label="avg sell"
              value={hasRealizedSells ? formatPrice(position.averageSellPrice) : '-'}
              tooltip="Average sell price across completed sells."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionDetails;
